import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ROWS = 6;
const COLS = 7;

export default function Connect4() {
  const [board, setBoard] = useState(
    Array(ROWS).fill(null).map(() => Array(COLS).fill(null))
  );
  const [currentPlayer, setCurrentPlayer] = useState('red'); // 'red' or 'yellow'
  const [winner, setWinner] = useState(null); // 'red', 'yellow', 'draw', or null
  const [winningCells, setWinningCells] = useState([]); // [{r, c}]

  const findAvailableRow = (colIndex, currentBoard) => {
    for (let r = 0; r < ROWS; r++) {
      if (currentBoard[r][colIndex] === null) {
        return r;
      }
    }
    return -1;
  };

  const checkWinner = (row, col, currentBoard, player) => {
    const directions = [
      [0, 1],  // Horizontal
      [1, 0],  // Vertical
      [1, 1],  // Diagonal /
      [1, -1], // Diagonal \
    ];

    for (const [rOffset, cOffset] of directions) {
      let count = 1;
      let pieces = [{ r: row, c: col }];

      // Forward direction
      for (let i = 1; i < 4; i++) {
        const r = row + i * rOffset;
        const c = col + i * cOffset;
        if (r >= 0 && r < ROWS && c >= 0 && c < COLS && currentBoard[r][c] === player) {
          count++;
          pieces.push({ r, c });
        } else {
          break;
        }
      }

      // Backward direction
      for (let i = 1; i < 4; i++) {
        const r = row - i * rOffset;
        const c = col - i * cOffset;
        if (r >= 0 && r < ROWS && c >= 0 && c < COLS && currentBoard[r][c] === player) {
          count++;
          pieces.push({ r, c });
        } else {
          break;
        }
      }

      if (count >= 4) {
        return pieces;
      }
    }
    return null;
  };

  const handleColumnClick = (colIndex) => {
    if (winner) return;

    const availableRow = findAvailableRow(colIndex, board);
    if (availableRow === -1) return; // Column is full

    const newBoard = board.map((row) => [...row]);
    newBoard[availableRow][colIndex] = currentPlayer;
    setBoard(newBoard);

    const winningPieces = checkWinner(availableRow, colIndex, newBoard, currentPlayer);
    if (winningPieces) {
      setWinner(currentPlayer);
      setWinningCells(winningPieces);
    } else {
      const isDraw = newBoard.every((row) => row.every((cell) => cell !== null));
      if (isDraw) {
        setWinner('draw');
      } else {
        setCurrentPlayer(currentPlayer === 'red' ? 'yellow' : 'red');
      }
    }
  };

  const restartGame = () => {
    setBoard(Array(ROWS).fill(null).map(() => Array(COLS).fill(null)));
    setCurrentPlayer('red');
    setWinner(null);
    setWinningCells([]);
  };

  const isWinningSlot = (r, c) => {
    return winningCells.some((cell) => cell.r === r && cell.c === c);
  };

  let statusMsg = '';
  if (winner === 'draw') {
    statusMsg = "It's a Draw!";
  } else if (winner) {
    statusMsg = `${winner.charAt(0).toUpperCase() + winner.slice(1)} Wins! 🎉`;
  } else {
    statusMsg = `${currentPlayer.charAt(0).toUpperCase() + currentPlayer.slice(1)}'s Turn`;
  }

  return (
    <main className="bg-gray-50 py-12 min-h-[calc(100vh-130px)] flex flex-col justify-between">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex flex-col items-center">
        <div className="w-full mb-6">
          <Link to="/projects" className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1">
            &larr; Back to Projects
          </Link>
        </div>

        <h1 className="text-4xl font-bold text-gray-900 mb-2">Connect 4</h1>
        <p className="text-lg text-gray-700 mb-6 font-semibold bg-white px-6 py-2 rounded-full shadow-sm border border-gray-200">
          {statusMsg}
        </p>

        {/* Connect 4 Board */}
        <div className="grid grid-cols-7 gap-2 sm:gap-3 bg-blue-600 p-3 sm:p-4 rounded-2xl shadow-2xl">
          {Array.from({ length: COLS }).map((_, c) => (
            <div
              key={c}
              onClick={() => handleColumnClick(c)}
              className="flex flex-col-reverse gap-2 sm:gap-3 cursor-pointer group hover:bg-blue-700/30 p-1 rounded-lg transition-colors"
            >
              {Array.from({ length: ROWS }).map((_, r) => {
                const cell = board[r][c];
                const winning = isWinningSlot(r, c);

                let cellColor = 'bg-gray-200';
                if (cell === 'red') cellColor = 'bg-red-500 shadow-inner';
                if (cell === 'yellow') cellColor = 'bg-amber-400 shadow-inner';

                return (
                  <div
                    key={r}
                    className={`w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 ${cellColor} ${
                      winning
                        ? 'ring-4 ring-emerald-400 animate-pulse scale-105'
                        : ''
                    } ${
                      !cell && !winner ? 'group-hover:opacity-90' : ''
                    }`}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <button
          onClick={restartGame}
          className="mt-8 bg-indigo-600 text-white font-semibold py-2.5 px-8 rounded-lg shadow-md hover:bg-indigo-700 active:scale-95 transition-all duration-200"
        >
          New Game
        </button>
      </div>
    </main>
  );
}
